/**
 * Advance for én act (PERFORMANCE.md, fane 6).
 *
 * ---------------------------------------------------------------------------------------------
 * THE WHOLE ADVANCE IN ONE ANSWER, BECAUSE THE PAGE DRAWS IT AS ONE LIST.
 *
 * «Overmappe med tre seksjoner i en seksjonsliste ... Hver seksjon viser kort status i listen, så
 * man ser hva som mangler uten å åpne.» A summary per section that is honest requires the section's
 * rows, so lazy-loading them per section would show three spinners where the point is one glance.
 * Every list here is bounded by a cap below.
 *
 * ---------------------------------------------------------------------------------------------
 * RIDER ≠ LEVERANSE, AND THE TYPES SAY SO.
 *
 * `AdvanceSectionDto.attachments` is the material from management — the rider, the stageplot, the
 * press photos. `AdvanceSectionDto.deliveries` is what the festival delivers. There is no link
 * between them and there is not meant to be: a rider asking for twelve wedges does not create
 * twelve rows, and a row nobody asked for is still something we deliver.
 *
 * ---------------------------------------------------------------------------------------------
 * ALLERGIES ARE NOT IN THIS CONTRACT, AND THAT IS PRINCIPLE 1.
 *
 * «Til slutt allergier og kosthold, knyttet til personer i reisefølget.» They are knyttet til
 * personer — they live on `performance_people`, which owns them and is where they are edited. The
 * Hospitality section READS the crew list to draw the catering basis. Repeating them here would be
 * the second copy the page exists to abolish.
 */
import { z } from 'zod';

import { urlInputSchema } from '../url.js';
import { contextAllocationSchema } from './resources.js';
import { wireInstant } from './wire-time.js';

/** Enough for a technical rider that lists every input. Beyond it, the rider is the document. */
export const MAX_DELIVERIES_PER_SECTION = 200;
export const MAX_SECTIONS_PER_ACT = 12;
export const MAX_LINKS_PER_SECTION = 20;
/**
 * How many agreed deviations one booking may hold.
 *
 * Generous, because the number is not a design opinion — it is a fence. A rider negotiation that
 * produced forty separate agreements is a real (miserable) booking; four hundred is a loop.
 */
export const MAX_DEVIATIONS_PER_ACT = 60;
export const MAX_DINNERS_PER_SECTION = 10;
export const MAX_DISHES_PER_DINNER = 30;
export const MAX_ADVANCE_TEXT = 200;
export const MAX_ADVANCE_NOTE = 2000;

/**
 * The three sections every act starts with. Keys, not names: the name is editable and the key is
 * what code recognises, which is what lets somebody rename «Teknisk» to «Produksjon» without
 * breaking the delivery categories that hang off `technical`.
 */
/**
 * `sortOrder` KOM MED #14, og den finnes fordi «sorteres» ikke virket uten den.
 *
 * Malen skrev `sortOrder` som sin egen indeks — promo 0, teknisk 1, hospitality 2 — mens skjermen
 * tegnet dem i en HELT ANNEN rekkefølge, hardkodet i en `sectionRank`-funksjon som satte promo sist
 * (Atle, 1. september: «Plasser promo nederst»). To rekkefølger for det samme, og den lagrede var
 * den ingen så.
 *
 * Da «Seksjonene kan … sorteres i den enkelte advancen» kom, ble det umulig: å flytte en seksjon
 * skriver `sort_order`, og skjermen leste den ikke. Nå er de ÉN rekkefølge — tallene her ER
 * standardrekkefølgen, og de er valgt slik at standarden fortsatt er promo nederst.
 *
 * Med mellomrom på ti, så en seksjon kan settes mellom to uten at alle må skrives om.
 */
export const ADVANCE_SECTION_TEMPLATE = [
  { key: 'technical', nb: 'Teknisk', en: 'Technical', sortOrder: 0 },
  { key: 'hospitality', nb: 'Hospitality', en: 'Hospitality', sortOrder: 10 },
  { key: 'promo', nb: 'Promo', en: 'Promo', sortOrder: 20 },
] as const;

export type AdvanceSectionTemplateKey = (typeof ADVANCE_SECTION_TEMPLATE)[number]['key'];

/**
 * The delivery groups a new organisation starts with, per kind of section.
 *
 * Straight from the spec: «Leveranserader gruppert i lyd, backline, lys og visuelt, strøm og
 * scene» for Teknisk, «Leveranserader for garderobe, drikke og øvrige behov» for Hospitality.
 * Promo gets one, because the pattern is «gjennomgående i alle tre» and a section with rows but no
 * groups would need a special case on every screen that draws them.
 *
 * DEFAULTS, NOT LAW. Principle 5: «kategorier for ... leveranser er data satt i Settings». These
 * are seeded per organisation and editable there; nothing in the code branches on a category.
 */
export const ADVANCE_DELIVERY_CATEGORY_TEMPLATE = [
  { section: 'promo', key: 'promo-material', nb: 'Promomateriell', en: 'Promo material' },
  { section: 'technical', key: 'sound', nb: 'Lyd', en: 'Sound' },
  { section: 'technical', key: 'backline', nb: 'Backline', en: 'Backline' },
  { section: 'technical', key: 'light', nb: 'Lys og visuelt', en: 'Light and visuals' },
  { section: 'technical', key: 'power', nb: 'Strøm', en: 'Power' },
  { section: 'technical', key: 'stage', nb: 'Scene', en: 'Stage' },
  { section: 'hospitality', key: 'dressing-room', nb: 'Garderobe', en: 'Dressing room' },
  { section: 'hospitality', key: 'drinks', nb: 'Drikke', en: 'Drinks' },
  { section: 'hospitality', key: 'other', nb: 'Øvrige behov', en: 'Other needs' },
] as const;

/**
 * How far a delivery has come. `pending` is the gap principle 2 wants visible; `unavailable` is the
 * supplier answering no, which is a different state from nobody having asked.
 */
export const ADVANCE_DELIVERY_STATUSES = [
  'pending',
  'ordered',
  'confirmed',
  'unavailable',
] as const;
export const advanceDeliveryStatusSchema = z.enum(ADVANCE_DELIVERY_STATUSES);
export type AdvanceDeliveryStatus = z.infer<typeof advanceDeliveryStatusSchema>;

/** «Planlagt releasedato og godkjenningsstatus.» */
export const PROMO_APPROVALS = ['draft', 'pending', 'approved', 'rejected'] as const;
export const promoApprovalSchema = z.enum(PROMO_APPROVALS);
export type PromoApproval = z.infer<typeof promoApprovalSchema>;

const shortText = z.string().trim().min(1).max(MAX_ADVANCE_TEXT);
const longText = z.string().trim().min(1).max(MAX_ADVANCE_NOTE);

/**
 * Http(s) only, and checked here as well as in the database.
 *
 * A link is rendered as an anchor, and `javascript:` in an href is the oldest hole there is. Zod
 * `url()` alone accepts any scheme, so the refinement is the point rather than decoration.
 *
 * SIDEN #16c ER DET IKKE LENGER TO REGLER. Den lokale refinementen gjorde halve jobben til
 * `urlInputSchema` — http(s) og ingenting annet — men krevde fortsatt at skjemaet var skrevet.
 * Nå gjør den delte modulen begge deler, og «lydanlegg.festival.no» er en gyldig lenke å lime inn
 * i en advance på samme måte som den er det på en festival.
 */
export const advanceUrlSchema = urlInputSchema(2000);

export const advanceDeliveryDtoSchema = z
  .object({
    id: z.uuid(),
    sectionId: z.uuid(),
    categoryId: z.uuid().nullable(),
    /** Resolved through the join, so a renamed category renames every row at once. */
    categoryName: z.string().nullable(),
    name: z.string(),
    specification: z.string().nullable(),
    supplierContactId: z.uuid().nullable(),
    supplierName: z.string().nullable(),
    status: advanceDeliveryStatusSchema,
    note: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'AdvanceDelivery' });
export type AdvanceDeliveryDto = z.infer<typeof advanceDeliveryDtoSchema>;

export const advanceLinkDtoSchema = z
  .object({
    id: z.uuid(),
    sectionId: z.uuid(),
    label: z.string(),
    url: z.string(),
    sortOrder: z.number().int(),
    /** En rider som lenke, og om arrangøren har godkjent den (QA13 75, 253.47, 0939). */
    rider: z.boolean(),
    approvedAt: z.iso.datetime().nullable(),
    approvedByName: z.string().nullable(),
    /** Hvem som la lenka inn — «filene merket med hvem som la dem inn» (75). */
    addedByName: z.string().nullable(),
  })
  .meta({ id: 'AdvanceLink' });
export type AdvanceLinkDto = z.infer<typeof advanceLinkDtoSchema>;

/**
 * Ett avklart avvik fra rideren (Atle, 2. september).
 *
 * The table's header has the argument for why this is a row rather than a note field: what matters
 * six weeks later is WHICH deviation, WHO agreed and WHEN, and one free-text box holds the words
 * and loses all three.
 */
export const advanceDeviationDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    detail: z.string().nullable(),
    agreedWith: z.string().nullable(),
    /** A calendar day, never an instant: nobody records the minute a deviation was accepted. */
    agreedOn: z.string().nullable(),
    sortOrder: z.number().int(),
    /**
     * «Må avklares på nytt» (QA15-S K1, 1270): the venue changed the room after this was agreed. Cleared
     * when the deviation is edited. Optional, so an older API does not break the contract.
     */
    reopenedAt: z.string().nullable().optional(),
  })
  .meta({ id: 'AdvanceDeviation' });
export type AdvanceDeviationDto = z.infer<typeof advanceDeviationDtoSchema>;

export const advanceDinnerDishDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    note: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'AdvanceDinnerDish' });
export type AdvanceDinnerDishDto = z.infer<typeof advanceDinnerDishDtoSchema>;

/** «Middagen skal dukke opp i timetable» — which is why `startsAt` is an instant, not a string. */
export const advanceDinnerDtoSchema = z
  .object({
    id: z.uuid(),
    sectionId: z.uuid(),
    startsAt: z.string().nullable(),
    /** Dag uten klokke (215.15): `startsAt` er midnatt i årgangens sone, klokka er ikke kjent. */
    startsDateOnly: z.boolean(),
    location: z.string().nullable(),
    pax: z.number().int().nullable(),
    /**
     * «HVEM SPISER» (253.39, 0925). `guestsAll`: hele reisefølget, og lista følger reisefølget.
     * Ellers er `guestIds` (rader i reisefølget og management) lista. `paxExtra` er tillegget
     * («+2 fra lokalt crew»), og `eaters` er antallet: lista + tillegget, aldri en erstatning.
     */
    guestsAll: z.boolean().optional(),
    guestIds: z.array(z.uuid()).optional(),
    paxExtra: z.number().int().optional(),
    eaters: z.number().int().optional(),
    note: z.string().nullable(),
    dishes: z.array(advanceDinnerDishDtoSchema),
    /** Sittingen måltidet er booket mot (§6, trinn 4). Null: `location`-friteksten gjelder. */
    allocation: contextAllocationSchema.nullable(),
  })
  .meta({ id: 'AdvanceDinner' });
export type AdvanceDinnerDto = z.infer<typeof advanceDinnerDtoSchema>;

/**
 * A delivery group, as the section's form offers it.
 *
 * PER ADVANCE SIDEN 19. SEPTEMBER (#14). Linja her sa «Org-level; edited in Settings», og siden
 * den viste til er fjernet: gruppene hører til den enkelte advancen, som er en avtale mellom to
 * hus, og ikke til festivalens innstillinger. Se skjemaet for hele begrunnelsen.
 */
export const advanceDeliveryCategoryDtoSchema = z
  .object({
    id: z.uuid(),
    sectionTemplateKey: z.string(),
    name: z.string(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'AdvanceDeliveryCategory' });
export type AdvanceDeliveryCategoryDto = z.infer<typeof advanceDeliveryCategoryDtoSchema>;

/**
 * The material from management, as the section lists it.
 *
 * A summary rather than the attachment rows themselves: the file list is `/v1/attachments`, which
 * already owns downloading, the virus gate and the quota. What the section needs to draw its own
 * heading is how many there are and when the newest arrived — «versjonshistorikk og hvem som lastet
 * opp» is the attachment list's own job, one click in.
 */
export const advanceAttachmentSummarySchema = z
  .object({
    count: z.number().int(),
    latestUploadedAt: wireInstant.nullable(),
    latestUploadedBy: z.string().nullable(),
  })
  .meta({ id: 'AdvanceAttachmentSummary' });
export type AdvanceAttachmentSummary = z.infer<typeof advanceAttachmentSummarySchema>;

export const advanceSectionDtoSchema = z
  .object({
    id: z.uuid(),
    templateKey: z.string().nullable(),
    name: z.string(),
    sortOrder: z.number().int(),
    documents: advanceAttachmentSummarySchema,
    categories: z.array(advanceDeliveryCategoryDtoSchema),
    deliveries: z.array(advanceDeliveryDtoSchema),
    links: z.array(advanceLinkDtoSchema),
    dinners: z.array(advanceDinnerDtoSchema),
  })
  .meta({ id: 'AdvanceSection' });
export type AdvanceSectionDto = z.infer<typeof advanceSectionDtoSchema>;

export const performanceAdvanceDtoSchema = z
  .object({
    performanceId: z.uuid(),
    /** Promo's own two fields. A calendar day: a release date is «den 4.» in every market. */
    promoReleaseOn: z.string().nullable(),
    promoApproval: promoApprovalSchema.nullable(),
    updatedAt: wireInstant.nullable(),
    sections: z.array(advanceSectionDtoSchema),
    /*
     * ON THE ACT, BESIDE THE SECTIONS RATHER THAN INSIDE ONE.
     *
     * A deviation is agreed against the rider as a whole. «Vi setter opp en ekstra wedge, men det
     * blir ingen backline-tech» is technical and hospitality at once, and forcing it into one of
     * them would be a filing decision the person writing it down does not have.
     */
    deviations: z.array(advanceDeviationDtoSchema),
  })
  .meta({ id: 'PerformanceAdvance' });
export type PerformanceAdvanceDto = z.infer<typeof performanceAdvanceDtoSchema>;

// ------------------------------------------------------------------------------------------- //
// Requests
// ------------------------------------------------------------------------------------------- //

export const updatePerformanceAdvanceRequestSchema = z
  .object({
    promoReleaseOn: z.iso.date().nullable(),
    promoApproval: promoApprovalSchema.nullable(),
  })
  .meta({ id: 'UpdatePerformanceAdvanceRequest' });
export type UpdatePerformanceAdvanceRequest = z.infer<typeof updatePerformanceAdvanceRequestSchema>;

export const createAdvanceSectionRequestSchema = z
  .object({
    performanceId: z.uuid(),
    name: shortText,
  })
  .meta({ id: 'CreateAdvanceSectionRequest' });
export type CreateAdvanceSectionRequest = z.infer<typeof createAdvanceSectionRequestSchema>;

/**
 * «Seksjonene kan gis nytt navn, SORTERES, legges til og fjernes i den enkelte advancen» (#14).
 *
 * `sortOrder` manglet, og uten den var «sorteres» det ene av de fire verbene ingen kunne gjøre.
 * Navnet er fortsatt det eneste som kan endres på en standardseksjon: `template_key` er 14a —
 * «Standardseksjonene bør ha en fast intern nøkkel, der bare visningsnavnet kan endres. Da havner
 * import fra artistprofilen (Teknisk, Hospitality, Promo) fortsatt riktig.»
 */
export const updateAdvanceSectionRequestSchema = z
  .object({
    name: shortText.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateAdvanceSectionRequest' });
export type UpdateAdvanceSectionRequest = z.infer<typeof updateAdvanceSectionRequestSchema>;

const deliveryFields = {
  categoryId: z.uuid().nullable(),
  name: shortText,
  specification: shortText.nullable(),
  supplierContactId: z.uuid().nullable(),
  status: advanceDeliveryStatusSchema,
  note: longText.nullable(),
};

export const createAdvanceDeliveryRequestSchema = z
  .object({ sectionId: z.uuid(), ...deliveryFields })
  .meta({ id: 'CreateAdvanceDeliveryRequest' });
export type CreateAdvanceDeliveryRequest = z.infer<typeof createAdvanceDeliveryRequestSchema>;

export const updateAdvanceDeliveryRequestSchema = z
  .object(deliveryFields)
  .meta({ id: 'UpdateAdvanceDeliveryRequest' });
export type UpdateAdvanceDeliveryRequest = z.infer<typeof updateAdvanceDeliveryRequestSchema>;

/**
 * What a supplier with a shared link may change: the status, and nothing else.
 *
 * «Status som leverandøren selv kan bekrefte når de har fått delt tilgang.» A separate request
 * shape rather than the full update, because a body that CAN carry a name is a body somebody will
 * eventually send a name in.
 */
export const setDeliveryStatusRequestSchema = z
  .object({ status: advanceDeliveryStatusSchema })
  .meta({ id: 'SetDeliveryStatusRequest' });
export type SetDeliveryStatusRequest = z.infer<typeof setDeliveryStatusRequestSchema>;

export const createAdvanceLinkRequestSchema = z
  .object({
    sectionId: z.uuid(),
    label: shortText,
    url: advanceUrlSchema,
    /** «Legg til rider» lager lenka med merket på (75). Standard: et vanlig vedlegg. */
    rider: z.boolean().optional(),
  })
  .meta({ id: 'CreateAdvanceLinkRequest' });
export type CreateAdvanceLinkRequest = z.infer<typeof createAdvanceLinkRequestSchema>;

export const updateAdvanceLinkRequestSchema = z
  .object({ label: shortText, url: advanceUrlSchema })
  .meta({ id: 'UpdateAdvanceLinkRequest' });
export type UpdateAdvanceLinkRequest = z.infer<typeof updateAdvanceLinkRequestSchema>;

const deviationFields = {
  title: shortText,
  detail: longText.nullable(),
  agreedWith: shortText.nullable(),
  agreedOn: z.iso.date().nullable(),
};

export const createAdvanceDeviationRequestSchema = z
  .object({ performanceId: z.uuid(), ...deviationFields })
  .meta({ id: 'CreateAdvanceDeviationRequest' });
export type CreateAdvanceDeviationRequest = z.infer<typeof createAdvanceDeviationRequestSchema>;

export const updateAdvanceDeviationRequestSchema = z
  .object(deviationFields)
  .meta({ id: 'UpdateAdvanceDeviationRequest' });
export type UpdateAdvanceDeviationRequest = z.infer<typeof updateAdvanceDeviationRequestSchema>;

/** Hvor mange som kan stå på «Hvem spiser» på én middag — samme tak som folkene på en tildeling. */
export const MAX_DINNER_GUESTS = 200;

const dinnerFields = {
  startsAt: z.iso.datetime().nullable(),
  /** 215.15: bare dagen i `startsAt` gjelder. Usendt er «med klokke», som før. */
  startsDateOnly: z.boolean().optional(),
  location: shortText.nullable(),
  pax: z.number().int().min(0).max(500).nullable(),
  note: longText.nullable(),
  /** «Hvem spiser» (253.39). Usendt lar det stå. */
  guestsAll: z.boolean().optional(),
  guestIds: z.array(z.uuid()).max(MAX_DINNER_GUESTS).optional(),
  paxExtra: z.number().int().min(0).max(500).optional(),
};

export const createAdvanceDinnerRequestSchema = z
  .object({ sectionId: z.uuid(), ...dinnerFields })
  .meta({ id: 'CreateAdvanceDinnerRequest' });
export type CreateAdvanceDinnerRequest = z.infer<typeof createAdvanceDinnerRequestSchema>;

/**
 * DELVIS (QA13 78): Oppsummering retter ett felt på en act sin middag med den samme PATCH-en som
 * skuffen. Et felt som ikke er med, står — før blanket en lagring av «Sted» notatet, som lesningen
 * der ikke har. Skuffen sender alle feltene, som før.
 */
export const updateAdvanceDinnerRequestSchema = z
  .object(dinnerFields)
  .partial()
  .meta({ id: 'UpdateAdvanceDinnerRequest' });
export type UpdateAdvanceDinnerRequest = z.infer<typeof updateAdvanceDinnerRequestSchema>;

/**
 * The whole dish list at once, the way a room's occupants are replaced.
 *
 * A menu is written as a menu — «laks, kjøttkaker, en vegetaroption» — and editing it row by row
 * would be three requests to type one course in. The list is capped, and the server diffs it.
 */
export const setDinnerDishesRequestSchema = z
  .object({
    dishes: z
      .array(z.object({ name: shortText, note: longText.nullable() }))
      .max(MAX_DISHES_PER_DINNER),
  })
  .meta({ id: 'SetDinnerDishesRequest' });
export type SetDinnerDishesRequest = z.infer<typeof setDinnerDishesRequestSchema>;

export const createDeliveryCategoryRequestSchema = z
  .object({
    /** Hvilken advance gruppen hører til (#14). Påkrevd: det finnes ikke lenger et org-sett. */
    performanceId: z.uuid(),
    sectionTemplateKey: shortText,
    name: shortText,
  })
  .meta({ id: 'CreateDeliveryCategoryRequest' });
export type CreateDeliveryCategoryRequest = z.infer<typeof createDeliveryCategoryRequestSchema>;

export const updateDeliveryCategoryRequestSchema = z
  .object({ name: shortText })
  .meta({ id: 'UpdateDeliveryCategoryRequest' });
export type UpdateDeliveryCategoryRequest = z.infer<typeof updateDeliveryCategoryRequestSchema>;

export const deliveryCategoryListResponseSchema = z
  .object({ items: z.array(advanceDeliveryCategoryDtoSchema) })
  .meta({ id: 'DeliveryCategoryListResponse' });
export type DeliveryCategoryListResponse = z.infer<typeof deliveryCategoryListResponseSchema>;

/**
 * How complete a section is, computed from its own rows — «hver seksjon viser kort status i listen,
 * så man ser hva som mangler uten å åpne».
 *
 * Shared rather than written twice, because the API's list summary and the panel's heading must
 * never disagree about what «3 av 8 bekreftet» means. `pending` and `ordered` are both outstanding;
 * `unavailable` is settled — the supplier answered, and the answer was no.
 */
export function summariseDeliveries(deliveries: readonly { status: AdvanceDeliveryStatus }[]): {
  total: number;
  confirmed: number;
  outstanding: number;
} {
  let confirmed = 0;
  let outstanding = 0;
  for (const row of deliveries) {
    if (row.status === 'confirmed') confirmed += 1;
    else if (row.status === 'pending' || row.status === 'ordered') outstanding += 1;
  }
  return { total: deliveries.length, confirmed, outstanding };
}
